import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const BulkOrderSection = ({ cartItems, onRequestBulkQuote }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [bulkRequirements, setBulkRequirements] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [companyName, setCompanyName] = useState('');

  const totalItems = cartItems?.reduce((sum, item) => sum + item?.quantity, 0);
  const totalValue = cartItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    })?.format(price);
  };

  const handleSubmitBulkRequest = () => {
    const bulkRequest = {
      items: cartItems,
      totalItems,
      totalValue,
      requirements: bulkRequirements,
      contactPerson,
      companyName,
      requestDate: new Date()?.toISOString()
    };

    onRequestBulkQuote(bulkRequest);
    setIsExpanded(false);
    setBulkRequirements('');
    setContactPerson('');
    setCompanyName('');
  };

  // Show bulk order section only if cart has significant value or quantity
  if (totalItems < 5 && totalValue < 5000000) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/20 rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Building" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Enterprise Bulk Order
            </h3>
            <p className="text-sm text-muted-foreground">
              Get special pricing for large orders
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1"
        >
          <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
          {isExpanded ? 'Collapse' : 'Expand'}
        </Button>
      </div>
      {/* Order Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="text-center p-3 bg-card rounded-lg">
          <div className="text-2xl font-bold text-primary">{totalItems}</div>
          <div className="text-sm text-muted-foreground">Total Items</div>
        </div>
        <div className="text-center p-3 bg-card rounded-lg">
          <div className="text-2xl font-bold text-primary">{formatPrice(totalValue)}</div>
          <div className="text-sm text-muted-foreground">Total Value</div>
        </div>
        <div className="text-center p-3 bg-card rounded-lg">
          <div className="text-2xl font-bold text-success">15%</div>
          <div className="text-sm text-muted-foreground">Potential Savings</div>
        </div>
        <div className="text-center p-3 bg-card rounded-lg">
          <div className="text-2xl font-bold text-accent">24h</div>
          <div className="text-sm text-muted-foreground">Quote Response</div>
        </div>
      </div>
      {/* Benefits */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="flex items-center gap-2 text-sm">
          <Icon name="Percent" size={16} className="text-success" />
          <span>Volume discounts up to 20%</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Icon name="Headphones" size={16} className="text-primary" />
          <span>Dedicated account manager</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Icon name="Calendar" size={16} className="text-accent" />
          <span>Flexible payment terms</span>
        </div>
      </div>
      {/* Expanded Form */}
      {isExpanded && (
        <div className="space-y-4 pt-4 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Company Name"
              type="text"
              placeholder="Enter your company name"
              value={companyName}
              onChange={(e) => setCompanyName(e?.target?.value)}
              required
            />
            <Input
              label="Contact Person"
              type="text"
              placeholder="Enter contact person name"
              value={contactPerson}
              onChange={(e) => setContactPerson(e?.target?.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Special Requirements
            </label>
            <textarea
              className="w-full p-3 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              rows={4}
              placeholder="Describe any special requirements, customizations, or timeline needs..."
              value={bulkRequirements}
              onChange={(e) => setBulkRequirements(e?.target?.value)}
            />
          </div>

          <div className="flex gap-3">
            <Button
              variant="default"
              onClick={handleSubmitBulkRequest}
              disabled={!companyName || !contactPerson}
              className="flex items-center gap-2"
            >
              <Icon name="Send" size={16} />
              Request Bulk Quote
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsExpanded(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkOrderSection;